import * as React from 'react';

import { useParams, useRouter } from 'js/lib/useRouter';

import { LoadingSection, PageGridContainer } from '@coursera/cds-core';

import { NotFound } from 'bundles/common/components/withNotFound';
import GrowthDiscovery from 'bundles/epic/clients/GrowthDiscovery';
import ReviewsHeader from 'bundles/reviews/components/ReviewsHeader';
import ReviewsPage from 'bundles/reviews/components/ReviewsPage';
import ReviewsTable from 'bundles/reviews/components/ReviewsTable';
import TopReviewsSection from 'bundles/reviews/components/TopReviewsSection';
import { useReviewsPageData } from 'bundles/reviews/hooks/useReviewsPageData';
import type { ReviewRouter } from 'bundles/reviews/types/reviewTypes';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

const NUM_TOP_REVIEWS_TO_SHOW = 12;

const ReviewsPageDirector = () => {
  const router = useRouter();
  const params = useParams();
  const slug = params?.slug ?? '';
  const programSlug = params?.programSlug;

  const {
    loading,
    error,
    notFound,
    courseInfo,
    description,
    ratings,
    topReviews = [],
    schemaReviews,
  } = useReviewsPageData();

  if (loading) {
    return <LoadingSection />;
  }

  if (!slug || notFound || error || !courseInfo?.name) {
    if (inServerContext) {
      throw new NotFoundError();
    }
    return <NotFound />;
  }

  const variant = GrowthDiscovery.get('reviewsPageRedesignXP');
  const coursePageHref = programSlug
    ? `/programs/${programSlug}/learn/${courseInfo.slug}`
    : `/learn/${courseInfo.slug}`;

  const pageProps = {
    slug,
    courseInfo,
    schemaReviews: schemaReviews ?? [],
    router: router as ReviewRouter,
  };

  if (variant === 'control') {
    return <ReviewsPage {...pageProps} />;
  }

  if (variant === 'variantB') {
    const courseId = courseInfo.id.split('~')[1];
    return (
      <PageGridContainer>
        <ReviewsHeader
          programSlug={programSlug}
          courseName={courseInfo.name}
          courseId={courseId ?? ''}
          coursePageHref={coursePageHref}
          ratings={ratings}
          description={description}
        />
        <TopReviewsSection topReviews={topReviews.slice(0, NUM_TOP_REVIEWS_TO_SHOW)} />
        <ReviewsTable courseId={courseInfo.id} />
      </PageGridContainer>
    );
  }

  if (variant === 'variantC') {
    return null;
  }

  return <ReviewsPage {...pageProps} />;
};

export default ReviewsPageDirector;
